import React, { useEffect, useRef, useState } from 'react'
import { StaticImage } from 'gatsby-plugin-image'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { MdClose, MdMenu, MdOutlinePerson, MdExpandLess, MdExpandMore } from 'react-icons/md'
import { Link, graphql, useStaticQuery } from 'gatsby'

import { getWindow } from 'services/window'
import { classNames } from 'services/string'

import { Container, IconButton, WhatsAppButton } from './'
import { useAnalytics } from 'hooks'

type NavbarItem = {
  label: string
  to: string
  partiallyActive?: boolean
}

interface IAppButtonProps {
  className?: string
  href: string
  onClick?: () => unknown
}

const AppButton = ({ className, href, onClick }: IAppButtonProps) => (
  <a href={href} rel='noreferrer noopener' target='_BLANK' className={className} onClick={onClick}>
    <div className='px-2.5 py-1.5 text-xs shadow-sm bg-primary-600 text-black rounded-full'>Área do Cliente</div>
  </a>
)

const options: NavbarItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Planos', to: '/#planos' },
  { label: 'Como funciona', to: '/como-funciona' },
  { label: 'Sobre', to: '/sobre' },
  { label: 'Parceiros', to: '/parceiros' },
  { label: 'Blog', to: '/blog', partiallyActive: true },
  { label: 'Vagas', to: '/vagas' },
  { label: 'Contato', to: '/contato' },
]

const calculators: NavbarItem[] = [
  { label: 'Fator R', to: '/calculadora-fator-r' },
  { label: 'PJ vs CLT', to: '/calculadora-pj-vs-clt' },
  { label: 'Custos para abrir CNPJ', to: '/calculadora-custos-cnpj' },
]

interface NavBarProps {
  disableWhatsApp?: boolean
  disableDefaultHeader?: boolean
}

const NavBar = ({ disableWhatsApp, disableDefaultHeader }: NavBarProps) => {
  const { fireEvent } = useAnalytics()
  const { strapiGlobal } = useStaticQuery(graphql`
    query {
      strapiGlobal {
        ...NavBarInfo
      }
    }
  `)

  const navBarInfo = strapiGlobal as Queries.NavBarInfoFragment

  const window = getWindow()

  const breakpoints = useBreakpoint()
  const lastScrollPosition = useRef<number>(window?.scrollY || window?.document.documentElement.scrollTop || 0)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const [toolsOpen, setToolsOpen] = useState<boolean>(false)
  const toolsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!toolsRef.current) return
      if (!toolsRef.current.contains(e.target as Node)) setToolsOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Mecanismo de ocultação do menu no scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!breakpoints.md) return
      const isScrolling = lastScrollPosition.current !== (window?.scrollY || 0)
      if (isOpen && isScrolling) return setIsOpen(false)
      lastScrollPosition.current = window?.scrollY || 0
    }
    window?.addEventListener('scroll', handleScroll)
    return () => window?.removeEventListener('scroll', handleScroll)
  }, [isOpen, breakpoints.md])

  // Mecanismo de ocultação do menu em caso de redimensionamento da tela
  useEffect(() => {
    if (!breakpoints.md) {
      setIsOpen(false)
    }
  }, [breakpoints.md])

  const getMenuDynamicStyles = () => (isOpen ? 'h-96 top-0' : 'h-0 -top-[30rem]')

  // Disparo do evento de clique externo
  const fireAnalyticsEvent = (target: string) => {
    fireEvent('send_contact', { component: 'NavBarWhatsappLink', target })

    fireEvent('externalClick', {
      component: 'NavBar',
      target,
    })
  }

  return (
    <nav>
      {!disableWhatsApp && <WhatsAppButton onClick={() => fireAnalyticsEvent('linkWhatsapp')} />}

      {disableDefaultHeader && (
        <div className='z-40 h-16 bg-black text-white w-full transition-all duration-300 relative'>
          <Container>
            <nav className='bg-black text-white h-24 flex justify-center sm:justify-start items-center w-full'>
              <StaticImage
                src='../images/logos/logo-white.png'
                alt='Logo Hubs Contabilidade'
                className='max-w-[200px]'
                placeholder='blurred'
                quality={100}
                width={150}
              />
            </nav>
          </Container>
        </div>
      )}

      {!disableDefaultHeader && (
        <>
          <div className='z-40 h-16 bg-black text-white w-full transition-all duration-300 relative'>
            <Container className='flex h-full items-center justify-between'>
              <IconButton
                aria-label='Botão para abrir e fechar o menu'
                className='border-none lg:hidden'
                icon={isOpen ? <MdClose aria-label='Fechar o Menu' size='1.5rem' /> : <MdMenu aria-label='Abrir o Menu' size='1.5rem' />}
                variant='outlined'
                color='inherit'
                onClick={() => setIsOpen(!isOpen)}
              />

              <Link to='/'>
                <StaticImage
                  src='../images/logos/logo-white.png'
                  alt='Logo Hubs Contabilidade'
                  className='max-w-[100px]'
                  placeholder='blurred'
                  quality={100}
                  width={100}
                />
              </Link>

              <div className='hidden lg:flex items-center'>
                {options.map(({ label, to, partiallyActive }, index) => (
                  <Link
                    partiallyActive={partiallyActive}
                    activeClassName='font-bold'
                    key={index}
                    to={to}
                    className='my-2 text-white px-3 py-2 text-xs lg:text-sm'
                  >
                    {label}
                  </Link>
                ))}

                <div className='relative' ref={toolsRef}>
                  <button
                    type='button'
                    aria-haspopup='menu'
                    aria-expanded={toolsOpen}
                    aria-controls='tools-menu'
                    onClick={() => setToolsOpen((v) => !v)}
                    className='my-2 inline-flex items-center gap-1 px-2 py-1'
                  >
                    <span className='text-white text-xs lg:text-sm'>Ferramentas</span>
                    {toolsOpen ? <MdExpandLess size='0.75rem' /> : <MdExpandMore size='0.75rem' />}
                  </button>

                  {toolsOpen && (
                    <div
                      id='tools-menu'
                      role='menu'
                      aria-label='Menu de ferramentas'
                      className='absolute right-0 mt-2 w-[20rem] origin-top-right rounded-xl border border-neutral-800 bg-neutral-900 p-2 shadow-2xl'
                    >
                      <div className='grid grid-cols-1 gap-1'>
                        {calculators.map(({ label, to }) => (
                          <Link
                            key={to}
                            role='menuitem'
                            to={to}
                            className='group flex items-center justify-between rounded-lg px-3 py-2'
                            onClick={() => setToolsOpen(false)}
                          >
                            <span className='text-white text-xs lg:text-sm'>{label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <a className='block lg:hidden w-fit' href={navBarInfo.linkAppWeb || ''} rel='noreferrer noopener' target='_BLANK'>
                <div className='flex flex-col justify-center items-center gap-1'>
                  <IconButton
                    color='primary'
                    size='sm'
                    icon={<MdOutlinePerson size='1rem' aria-label='Botão para área de Cliente da Hubs' />}
                    aria-label='Botão para acessar a Área de Cliente da Hubs'
                  />
                  <p className='text-xs'>Cliente</p>
                </div>
              </a>

              <AppButton className='hidden lg:block' href={navBarInfo.linkAppWeb || ''} />
            </Container>
          </div>

          <div
            className={classNames(
              getMenuDynamicStyles(),
              'block lg:hidden mt-16 w-full bg-black text-white fixed z-30 rounded-b-xl transition-all duration-300 shadow-md max-h-[calc(100dvh-4rem)] overflow-y-auto',
            )}
          >
            <div className='container mx-auto py-4 pb-6 flex flex-col gap-4 transition-all min-h-full'>
              <div className='flex flex-col'>
                {options.map(({ label, to, partiallyActive }, index) => (
                  <Link
                    partiallyActive={partiallyActive}
                    activeClassName='font-bold'
                    key={index}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className='text-white px-3 py-2 text-sm lg:text-sm'
                  >
                    {label}
                  </Link>
                ))}

                <details className='group'>
                  <summary className='inline-flex items-center'>
                    <span className='text-white px-3 py-2 text-sm lg:text-sm'>Ferramentas</span>
                    <span>
                      <MdExpandMore className='group-open:hidden' size='0.75rem' />
                      <MdExpandLess className='hidden group-open:inline' size='0.75rem' />
                    </span>
                  </summary>

                  <div className='flex flex-col justify-between space-y-1 pl-8'>
                    {calculators.map(({ label, to }) => (
                      <Link key={to} to={to} onClick={() => setIsOpen(false)}>
                        <span className='text-white text-sm'>{label}</span>
                      </Link>
                    ))}
                  </div>
                </details>
              </div>

              <AppButton className='self-start' href={navBarInfo.linkAppWeb || ''} />
            </div>
          </div>
        </>
      )}
    </nav>
  )
}

export const query = graphql`
  fragment NavBarInfo on STRAPI_GLOBAL {
    id
    linkAppWeb
  }
`

export default NavBar
